/**
 * Help content for the Pax8 Licensing Portal
 * This file contains all help articles organized by category
 *
 * IMPORTANT: Update this file whenever features are added, removed, or changed!
 */

import { APP_VERSION } from '../version';

// Help article interface
export interface HelpArticle {
  id: string;
  title: string;
  category: HelpCategory;
  summary: string;
  content: string;
  keywords: string[];
  relatedArticles?: string[];
}

export type HelpCategory =
  | 'getting-started'
  | 'dashboard'
  | 'clients'
  | 'subscriptions'
  | 'users'
  | 'groups'
  | 'alerts'
  | 'reports'
  | 'audit-logs'
  | 'troubleshooting';

export interface CategoryInfo {
  id: HelpCategory;
  name: string;
  description: string;
  icon: string;
}

// Category metadata
export const categories: CategoryInfo[] = [
  {
    id: 'getting-started',
    name: 'Getting Started',
    description: 'Learn the basics of the Pax8 Licensing Portal',
    icon: 'RocketLaunchIcon',
  },
  {
    id: 'dashboard',
    name: 'Dashboard',
    description: 'Understanding your dashboard metrics and sync status',
    icon: 'HomeIcon',
  },
  {
    id: 'clients',
    name: 'Clients',
    description: 'Managing client information and subscriptions',
    icon: 'UserGroupIcon',
  },
  {
    id: 'subscriptions',
    name: 'Subscriptions',
    description: 'Tracking and managing subscriptions',
    icon: 'CreditCardIcon',
  },
  {
    id: 'users',
    name: 'User Management',
    description: 'Managing tenant users, licenses, and offboarding',
    icon: 'UserIcon',
  },
  {
    id: 'groups',
    name: 'Groups',
    description: 'Viewing and managing tenant groups',
    icon: 'UsersIcon',
  },
  {
    id: 'alerts',
    name: 'Alerts',
    description: 'Understanding and managing alerts',
    icon: 'BellAlertIcon',
  },
  {
    id: 'reports',
    name: 'Reports',
    description: 'Generating and understanding reports',
    icon: 'DocumentChartBarIcon',
  },
  {
    id: 'audit-logs',
    name: 'Audit Logs',
    description: 'Tracking changes and sync history',
    icon: 'ClipboardDocumentListIcon',
  },
  {
    id: 'troubleshooting',
    name: 'Troubleshooting',
    description: 'Common issues and how to resolve them',
    icon: 'WrenchScrewdriverIcon',
  },
];

// All help articles
export const helpArticles: HelpArticle[] = [
  // GETTING STARTED
  {
    id: 'what-is-pax8-licensing',
    title: 'What is Pax8 Licensing Portal?',
    category: 'getting-started',
    summary: 'An overview of the Pax8 Licensing Portal and its purpose.',
    content: `The Pax8 Licensing Portal is a comprehensive management tool for MSPs (Managed Service Providers) to track and manage Microsoft 365 and other cloud subscriptions for their clients.

**Key Features:**
- **Client Management**: Track all your clients and their subscriptions in one place
- **License Tracking**: See how many licenses are purchased vs. actually used
- **Subscription Monitoring**: Track renewals, cancellations, and billing status
- **User Management**: Manage tenant users, assign licenses, reset passwords, and offboard users
- **Alerts**: Get notified about upcoming renewals and potential issues
- **Reports**: Generate utilization and renewal reports

**Data Sources:**
- **Pax8 API**: Syncs subscription data, pricing, and renewal dates from Pax8
- **Microsoft Graph API via GDAP**: Syncs license utilization data directly from customer tenants`,
    keywords: ['overview', 'introduction', 'what is', 'about', 'purpose', 'features'],
    relatedArticles: ['sync-pax8', 'sync-gdap'],
  },
  {
    id: 'navigation-basics',
    title: 'Navigating the Portal',
    category: 'getting-started',
    summary: 'How to navigate around the Pax8 Licensing Portal.',
    content: `The portal has a simple sidebar navigation on the left side.

**Main Sections:**
- **Dashboard**: Overview of key metrics, alerts, and upcoming renewals
- **Clients**: List of all clients with their subscription counts and license usage
- **Subscriptions**: All subscriptions across all clients with filtering options
- **Alerts**: Active alerts about renewals, billing issues, and other concerns
- **Reports**: Renewal and utilization reports
- **Audit Log**: History of changes and sync operations

**Mobile Navigation:**
On mobile devices, tap the hamburger menu (☰) in the top left to open the sidebar.

**Version Info:**
The current version is shown in the sidebar and in the user menu. Click it to view the changelog.`,
    keywords: ['navigation', 'menu', 'sidebar', 'mobile', 'layout'],
    relatedArticles: ['what-is-pax8-licensing'],
  },

  // DASHBOARD
  {
    id: 'dashboard-overview',
    title: 'Dashboard Overview',
    category: 'dashboard',
    summary: 'Understanding your dashboard metrics and what they mean.',
    content: `The dashboard provides a quick overview of your licensing status.

**Top Row Metrics:**
- **Total Clients**: Number of clients in your Pax8 account
- **Total Licenses**: Total purchased licenses across all subscriptions
- **Monthly Revenue**: Estimated monthly recurring revenue
- **Active Subscriptions**: Number of active subscriptions

**Bottom Row Metrics:**
- **Renewals (30 days)**: Subscriptions renewing soon that may need attention
- **At-Risk Clients**: Clients with payment issues or other concerns
- **Pending Cancellations**: Cancellation requests awaiting verification

**Additional Sections:**
- **Recent Alerts**: Latest alerts requiring attention
- **Renewal Calendar**: Upcoming renewals with subscription details

Click any stat card to drill down into the relevant page with pre-applied filters.`,
    keywords: ['dashboard', 'stats', 'metrics', 'overview', 'revenue', 'licenses'],
    relatedArticles: ['sync-pax8', 'sync-gdap', 'alerts-overview'],
  },
  {
    id: 'sync-pax8',
    title: 'Sync Pax8 Data',
    category: 'dashboard',
    summary: 'How to sync subscription data from Pax8.',
    content: `The "Sync Pax8" button refreshes subscription data from the Pax8 API.

**What Gets Synced:**
- Subscription details (product name, SKU, quantity)
- Pricing information
- Renewal/commitment dates
- Auto-renew status
- Client/company information

**When to Use:**
- After making changes in the Pax8 portal
- When subscription data appears outdated
- After adding new subscriptions

**Automatic Sync:**
Pax8 data is automatically synced once per day. The last sync time is shown in the dashboard header.

**Sync Status:**
A green checkmark indicates successful sync. A red X indicates an error.`,
    keywords: ['sync', 'pax8', 'refresh', 'update', 'data'],
    relatedArticles: ['sync-gdap', 'dashboard-overview'],
  },
  {
    id: 'sync-gdap',
    title: 'Sync GDAP Data',
    category: 'dashboard',
    summary: 'How to sync license utilization data from Microsoft tenants.',
    content: `The "Sync GDAP" button refreshes license utilization data from customer Microsoft 365 tenants via GDAP (Granular Delegated Admin Privileges).

**What Gets Synced:**
- License assignments (who has what license)
- License consumption (used vs. available)
- SKU information
- Microsoft Direct subscriptions

**Requirements:**
- Active GDAP relationship with customer tenant
- Appropriate admin roles granted via GDAP

**When to Use:**
- To get current license utilization
- After assigning/removing user licenses
- When utilization data seems stale

**Troubleshooting:**
If GDAP sync fails for a client, check that the GDAP relationship is active and the necessary roles are granted.`,
    keywords: ['sync', 'gdap', 'microsoft', 'utilization', 'licenses', 'assigned'],
    relatedArticles: ['sync-pax8', 'license-utilization'],
  },

  // CLIENTS
  {
    id: 'clients-list',
    title: 'Client List',
    category: 'clients',
    summary: 'Viewing and filtering your client list.',
    content: `The Clients page shows all clients from your Pax8 account.

**Client Information:**
- Company name
- Number of active subscriptions
- License count (purchased vs. assigned)
- Status badges (at-risk, attention needed)

**Filtering Options:**
- **Search**: Find clients by name
- **Status**: Filter by All, Active, or At-Risk
- **License Gap**: Find clients with unmatched licenses

**Client Cards:**
Click any client to view their detail page with subscriptions, users, and groups.

**Quick Stats:**
Each client card shows a license summary: "X of Y assigned" where X is assigned licenses and Y is total purchased.`,
    keywords: ['clients', 'list', 'filter', 'search', 'customers'],
    relatedArticles: ['client-detail', 'license-utilization'],
  },
  {
    id: 'client-detail',
    title: 'Client Detail Page',
    category: 'clients',
    summary: 'Understanding the client detail page tabs and features.',
    content: `The client detail page shows comprehensive information about a single client.

**Header Information:**
- Client name and status
- Tenant ID (if GDAP connected)
- Quick action buttons

**Tabs:**

**Subscriptions Tab:**
- All subscriptions for this client
- Source badges (Pax8 vs MS Direct)
- Quantity and pricing
- Renewal status and dates

**Users Tab:**
- All users in the tenant
- License assignments per user
- Click a user to manage their licenses, groups, and more

**Groups Tab:**
- All groups in the tenant
- Security, Microsoft 365, and Distribution groups
- Member counts

**Sync Buttons:**
- "Sync Pax8": Refresh just this client's Pax8 data
- "Sync MS": Refresh just this client's GDAP data`,
    keywords: ['client', 'detail', 'subscriptions', 'users', 'groups', 'tabs'],
    relatedArticles: ['clients-list', 'user-management', 'groups-list'],
  },

  // SUBSCRIPTIONS
  {
    id: 'subscriptions-list',
    title: 'Subscriptions List',
    category: 'subscriptions',
    summary: 'Viewing and filtering subscriptions across all clients.',
    content: `The Subscriptions page shows all subscriptions across all clients.

**Subscription Information:**
- Product name and SKU
- Client name
- Quantity (licenses)
- Source (Pax8 or MS Direct)
- Auto-renew status
- Commitment/renewal dates

**Filtering Options:**
- **Search**: Find by product name
- **Customer**: Filter by specific client
- **Status**: Active, Cancelled, Suspended
- **Source**: Pax8 only, MS Direct only
- **Renewal Period**: Renewals within X days

**Subscription Sources:**
- **Pax8**: Purchased through Pax8 reseller portal
- **MS Direct**: Purchased directly from Microsoft or via Microsoft Admin Center`,
    keywords: ['subscriptions', 'list', 'filter', 'products', 'licenses'],
    relatedArticles: ['subscription-detail', 'auto-renew'],
  },
  {
    id: 'subscription-detail',
    title: 'Subscription Detail',
    category: 'subscriptions',
    summary: 'Understanding subscription details and available actions.',
    content: `Click any subscription to view its full details.

**Subscription Information:**
- **SKU/Product**: Full product name and SKU ID
- **Quantity**: Number of licenses
- **Billing Term**: Monthly or Annual billing
- **Commitment Term**: How long you're committed (Monthly vs 1-Year)
- **Auto-Renew Status**: Recurring On/Off/Unknown

**Key Dates:**
- **Pax8 Commitment End**: When your Pax8 pricing term expires
- **MS Expiration Date**: When Microsoft will renew/expire the subscription

**License Utilization:**
Shows how many licenses are actually assigned to users vs. purchased.

**Modification History:**
Shows a log of changes made to this subscription with timestamps.

**Manual Auto-Renew Toggle:**
You can manually mark a subscription as Recurring On or Off if the automatic detection isn't working.`,
    keywords: ['subscription', 'detail', 'renewal', 'billing', 'commitment'],
    relatedArticles: ['subscriptions-list', 'auto-renew'],
  },
  {
    id: 'auto-renew',
    title: 'Auto-Renew / Recurring Status',
    category: 'subscriptions',
    summary: 'Understanding and managing subscription auto-renewal status.',
    content: `The auto-renew (Recurring) status indicates whether a subscription will automatically renew.

**Status Values:**
- **Recurring: On** (Green): Subscription will auto-renew
- **Recurring: Off** (Orange): Subscription will expire/cancel
- **Unknown** (Gray): Status couldn't be determined

**Why Unknown?**
- Partner Center API limitations for indirect resellers
- GDAP sync hasn't run for this tenant
- New subscription not yet synced

**Manual Override:**
On the subscription detail page, you can manually set the recurring status using the "Set to On" or "Set to Off" buttons.

**Checking Microsoft Admin Center:**
For the most accurate status, have the customer check their Microsoft 365 Admin Center → Billing → Your products → Subscription details.`,
    keywords: ['auto-renew', 'recurring', 'renewal', 'cancel', 'expiration'],
    relatedArticles: ['subscription-detail', 'alerts-renewal'],
  },
  {
    id: 'pax8-vs-ms-direct',
    title: 'Pax8 vs MS Direct Subscriptions',
    category: 'subscriptions',
    summary: 'Understanding the difference between Pax8 and Microsoft Direct subscriptions.',
    content: `Subscriptions can come from two sources:

**Pax8 Subscriptions:**
- Purchased through the Pax8 reseller portal
- Managed and billed by Pax8
- May have different pricing than Microsoft direct
- Shows Pax8-specific fields (commitment end, pricing)

**MS Direct Subscriptions:**
- Purchased directly from Microsoft
- Billed directly by Microsoft
- Found via GDAP sync from tenant
- May not have Pax8 coverage

**License Coverage:**
The License Coverage view on client detail shows which Microsoft licenses are covered by Pax8 subscriptions vs. unmatched MS Direct purchases.

**Managing MS Direct:**
MS Direct subscriptions are managed in the Microsoft 365 Admin Center, not Pax8. Cancellation and renewal changes must be made there.`,
    keywords: ['pax8', 'microsoft', 'direct', 'source', 'coverage'],
    relatedArticles: ['subscription-detail', 'license-utilization'],
  },

  // USER MANAGEMENT
  {
    id: 'user-management',
    title: 'Managing Tenant Users',
    category: 'users',
    summary: 'How to view and manage users in customer tenants.',
    content: `The Users tab on the client detail page shows all users in that tenant.

**User List:**
- User display name and email
- Account status (enabled/disabled)
- Assigned licenses
- Last sign-in time (if available)

**Filtering:**
- Search by name or email
- Filter by license assignment

**Click a User to:**
- View detailed user information
- Manage their licenses
- View group memberships
- View directory roles
- Take actions (reset password, enable/disable, offboard)

**Requirements:**
- Active GDAP relationship with the tenant
- Appropriate admin roles (User Administrator, License Administrator, etc.)`,
    keywords: ['users', 'tenant', 'management', 'list', 'licenses'],
    relatedArticles: ['user-licenses', 'user-offboarding', 'client-detail'],
  },
  {
    id: 'user-licenses',
    title: 'Assigning and Removing User Licenses',
    category: 'users',
    summary: 'How to assign or remove licenses from tenant users.',
    content: `On the user detail modal, the Licenses tab shows all assigned licenses and available licenses.

**Viewing Assigned Licenses:**
The top section shows licenses currently assigned to this user.

**Removing a License:**
1. Click the red "Remove" button next to a license
2. Confirm the removal
3. License is immediately removed and freed up for other users

**Assigning a License:**
1. Scroll to "Available Licenses" section
2. Click the green "Assign" button next to a license
3. License is immediately assigned to the user

**License Availability:**
Only licenses with available seats (not all assigned) appear in the Available Licenses section.

**Common Scenarios:**
- Upgrading: Assign new license first, then remove old one
- Offboarding: Use the Offboard workflow to remove all licenses at once`,
    keywords: ['licenses', 'assign', 'remove', 'users', 'sku'],
    relatedArticles: ['user-management', 'user-offboarding', 'license-utilization'],
  },
  {
    id: 'user-password-reset',
    title: 'Resetting User Passwords',
    category: 'users',
    summary: 'How to reset a user\'s password.',
    content: `On the user detail modal, the Actions tab allows you to reset passwords.

**Reset Password:**
1. Go to the Actions tab
2. Click "Reset Password"
3. A temporary password is generated
4. Copy the password and provide it to the user

**Important Notes:**
- The user will be required to change their password on next sign-in
- The temporary password is shown only once - copy it immediately
- This operation is logged in the audit log

**Permissions Required:**
User Administrator or Password Administrator role via GDAP.`,
    keywords: ['password', 'reset', 'temporary', 'user', 'security'],
    relatedArticles: ['user-management', 'user-enable-disable'],
  },
  {
    id: 'user-enable-disable',
    title: 'Enabling and Disabling User Accounts',
    category: 'users',
    summary: 'How to enable or disable (block) user sign-in.',
    content: `On the user detail modal, the Actions tab allows you to enable or disable accounts.

**Disable Account (Block Sign-In):**
1. Go to the Actions tab
2. Click "Disable Account"
3. User is immediately blocked from signing in
4. Existing sessions may remain active until they expire

**Enable Account:**
1. Go to the Actions tab
2. Click "Enable Account"
3. User can sign in again (with their existing password)

**Use Cases for Disabling:**
- Temporary leave of absence
- Security concerns
- Before offboarding (included in offboard workflow)

**Permissions Required:**
User Administrator role via GDAP.`,
    keywords: ['enable', 'disable', 'block', 'sign-in', 'account', 'security'],
    relatedArticles: ['user-management', 'user-offboarding'],
  },
  {
    id: 'user-offboarding',
    title: 'Offboarding Users',
    category: 'users',
    summary: 'The complete user offboarding workflow.',
    content: `The Offboard Wizard guides you through all steps to properly offboard a departing user.

**Starting Offboard:**
1. Click on a user in the Users tab
2. Go to the Actions tab
3. Click "Offboard User" (red button)

**Multi-Step Wizard:**

**Step 1 - Confirm User:**
- Review the user being offboarded
- See their current licenses and group count
- Warning message to verify stakeholder approval

**Step 2 - Select Operations:**
Choose which operations to perform:

*Account Actions:*
- Block sign-in (prevent future access)
- Revoke active sessions (immediate sign-out)
- Reset password to random value

*Mailbox Actions (Automated via Exchange Online):*
- Convert mailbox to shared (retain without license cost)
- Grant full access to delegate
- Hide from Global Address List (GAL)

*Data Actions:*
- Share OneDrive with delegate

**Step 3 - Select Delegate (if needed):**
- Choose a user to receive mailbox/OneDrive access
- Search from list of tenant users

**Step 4 - OneDrive Access (if sharing):**
- Choose access level: Read, Write, or Owner
- Delegate will be granted access

**Step 5 - Review:**
- See summary of all selected operations
- Confirm before executing

**Step 6 - Progress:**
- Watch operations complete in real-time
- See success/error status for each operation
- Exchange operations may take longer (automated via Azure)

**What Gets Freed Up:**
- All licenses are removed and returned to the pool
- Shared mailboxes don't require a license
- Data is preserved (OneDrive for 30 days, shared mailbox indefinitely)`,
    keywords: ['offboard', 'offboarding', 'departure', 'leave', 'terminate', 'onedrive', 'mailbox', 'shared', 'delegate', 'wizard'],
    relatedArticles: ['user-licenses', 'user-enable-disable', 'user-management'],
  },

  // GROUPS
  {
    id: 'groups-list',
    title: 'Viewing Tenant Groups',
    category: 'groups',
    summary: 'How to view groups and members in customer tenants.',
    content: `The Groups tab on the client detail page shows all groups in that tenant.

**Group Types:**
- **Security Groups**: Used for access control and permissions
- **Microsoft 365 Groups**: Collaboration groups with shared resources (Teams, SharePoint)
- **Distribution Groups**: Email distribution lists

**Group Information:**
- Group name and icon
- Group type badge (color-coded)
- Mail-enabled / Security-enabled properties
- Description

**Filtering:**
- Search by group name or description
- Filter by group type dropdown

**Viewing Members:**
Click any group row to open the members modal:
- See all members with their display name and email
- Member type icons (user, group, device, service principal)
- Handles groups with many members

**Group Properties:**
- Mail-enabled groups can receive email
- Security-enabled groups can be used for permissions
- Microsoft 365 groups combine both with collaboration features`,
    keywords: ['groups', 'security', 'distribution', 'm365', 'teams', 'members', 'membership'],
    relatedArticles: ['client-detail', 'user-management'],
  },

  // ALERTS
  {
    id: 'alerts-overview',
    title: 'Understanding Alerts',
    category: 'alerts',
    summary: 'What alerts mean and how to manage them.',
    content: `Alerts notify you about important events requiring attention.

**Alert Types:**
- **Renewal**: Subscription renewal is approaching
- **Payment**: Payment or billing issues
- **Cancellation**: Subscription cancellation in progress
- **Utilization**: License utilization concerns

**Alert Severity:**
- **Critical** (Red): Immediate action required
- **Warning** (Yellow): Action needed soon
- **Info** (Blue): For your information

**Alert Status:**
- **Open**: Needs attention
- **Acknowledged**: You've seen it, working on it
- **Resolved**: Issue is fixed

**Managing Alerts:**
Click an alert to view details and update its status.`,
    keywords: ['alerts', 'notifications', 'warnings', 'critical', 'action'],
    relatedArticles: ['alerts-renewal', 'dashboard-overview'],
  },
  {
    id: 'alerts-renewal',
    title: 'Renewal Alerts',
    category: 'alerts',
    summary: 'Understanding subscription renewal alerts.',
    content: `Renewal alerts help you stay ahead of subscription renewals.

**When Generated:**
- 90 days before renewal (Info)
- 60 days before renewal (Warning)
- 30 days before renewal (Critical)

**What to Check:**
1. Is auto-renew enabled? If not, the subscription may expire
2. Has the customer confirmed they want to continue?
3. Are there any pricing changes for the renewal?
4. Should quantity be adjusted?

**Alert Suppression:**
Renewal alerts are automatically suppressed for Pax8 subscriptions with auto-renew enabled, as these will renew without intervention.

**Taking Action:**
Review the subscription, contact the customer if needed, and acknowledge the alert once handled.`,
    keywords: ['renewal', 'expiring', '30 days', '60 days', '90 days'],
    relatedArticles: ['alerts-overview', 'auto-renew'],
  },

  // REPORTS
  {
    id: 'reports-overview',
    title: 'Available Reports',
    category: 'reports',
    summary: 'Overview of available reports and what they show.',
    content: `The Reports page provides several useful reports.

**Renewal Report:**
Lists all subscriptions by renewal date. Useful for:
- Planning renewal conversations
- Identifying upcoming commitments
- Forecasting revenue changes

**Utilization Report:**
Shows license utilization across clients. Useful for:
- Finding over-provisioned licenses
- Identifying optimization opportunities
- Right-sizing recommendations

**Report Features:**
- Sort by any column
- Export to CSV (coming soon)
- Filter by date range or client

**Best Practices:**
- Run utilization reports monthly to identify savings
- Review renewal reports weekly to stay ahead of renewals`,
    keywords: ['reports', 'renewal', 'utilization', 'export', 'csv'],
    relatedArticles: ['license-utilization', 'subscriptions-list'],
  },
  {
    id: 'license-utilization',
    title: 'License Utilization',
    category: 'reports',
    summary: 'Understanding license utilization and optimization.',
    content: `License utilization shows how many licenses are actually being used vs. purchased.

**Reading Utilization:**
- **Assigned**: Licenses currently assigned to users
- **Purchased**: Total licenses in the subscription
- **Utilization %**: Assigned ÷ Purchased × 100

**Ideal Utilization:**
- 80-95% is healthy (room for growth)
- Below 70% suggests over-provisioning
- At 100% means no room for new users

**Over-Provisioning:**
If utilization is low, consider:
- Reducing quantity at next renewal
- Consolidating subscriptions
- Reviewing if all users need the license

**Under-Provisioning:**
If at 100%, you may need to:
- Increase quantity
- Remove licenses from inactive users
- Consider alternative products`,
    keywords: ['utilization', 'assigned', 'purchased', 'optimization', 'over-provisioned'],
    relatedArticles: ['reports-overview', 'user-licenses'],
  },

  // AUDIT LOGS
  {
    id: 'audit-logs-overview',
    title: 'Audit Logs',
    category: 'audit-logs',
    summary: 'Tracking changes and operations in the portal.',
    content: `The Audit Log page shows a history of all changes and operations.

**What's Logged:**
- Subscription changes (quantity, status, dates)
- User management actions (password resets, license changes)
- Sync operations (Pax8, GDAP)
- Alert status changes
- Admin actions

**Log Entry Details:**
- Timestamp
- Action type
- Target (subscription, user, client)
- Before/after values (for changes)
- Who performed the action

**Filtering:**
- Search by keyword
- Filter by action type
- Filter by date range

**Sync History Tab:**
Shows detailed history of Pax8 and GDAP sync operations with success/failure status.`,
    keywords: ['audit', 'log', 'history', 'changes', 'tracking'],
    relatedArticles: ['sync-pax8', 'sync-gdap'],
  },

  // TROUBLESHOOTING
  {
    id: 'troubleshooting-gdap',
    title: 'GDAP Sync Issues',
    category: 'troubleshooting',
    summary: 'Resolving GDAP sync failures and access issues.',
    content: `If GDAP sync is failing for a client, check these common issues:

**GDAP Relationship Not Active:**
1. Verify GDAP relationship exists in Partner Center
2. Confirm customer has approved the relationship
3. Check relationship hasn't expired

**Missing Admin Roles:**
Required roles for full functionality:
- Directory Readers (basic access)
- License Administrator (manage licenses)
- User Administrator (manage users)
- Groups Administrator (manage groups)

**Tenant Not Linked:**
1. Go to Client Detail
2. Check if Tenant ID is set
3. If not, verify client is properly matched to their tenant

**Permission Errors:**
- Check the Azure AD app has the required API permissions
- Verify admin consent has been granted`,
    keywords: ['gdap', 'sync', 'error', 'permission', 'access', 'denied'],
    relatedArticles: ['sync-gdap', 'client-detail'],
  },
  {
    id: 'troubleshooting-missing-subs',
    title: 'Missing Subscriptions',
    category: 'troubleshooting',
    summary: 'Why subscriptions might not appear in the portal.',
    content: `If subscriptions are missing, check these common causes:

**Pax8 Subscriptions Not Showing:**
1. Click "Sync Pax8" to refresh data
2. Check if subscription is marked as Cancelled/Suspended in Pax8
3. Verify the subscription is under your Pax8 partner account

**MS Direct Subscriptions Not Showing:**
1. Click "Sync MS" to refresh GDAP data
2. Verify GDAP relationship is active
3. Check if subscription is in the correct tenant

**Recently Purchased:**
New subscriptions may take up to 24 hours to appear. Force sync if needed.

**Wrong Client:**
If a subscription appears under the wrong client, the Pax8 company ID may be mapped incorrectly.`,
    keywords: ['missing', 'subscriptions', 'not showing', 'disappeared'],
    relatedArticles: ['sync-pax8', 'sync-gdap', 'subscriptions-list'],
  },
  {
    id: 'troubleshooting-user-detail',
    title: 'User Detail Not Loading',
    category: 'troubleshooting',
    summary: 'Resolving issues when user details fail to load.',
    content: `If user details fail to load, try these solutions:

**Permission Issues:**
- Ensure you have User Administrator or Global Reader role via GDAP
- Some properties require Azure AD Premium (sign-in activity)

**Azure AD Premium Required:**
Sign-in activity data requires Azure AD Premium P1 or P2. Without it, sign-in times won't be shown but other data works.

**Tenant Not Accessible:**
- Verify GDAP relationship is active
- Check tenant ID is correct
- Try syncing GDAP data again

**Browser Cache:**
Clear browser cache and reload the page.`,
    keywords: ['user', 'detail', 'loading', 'error', 'permission'],
    relatedArticles: ['user-management', 'troubleshooting-gdap'],
  },
];

/**
 * Search help articles by query
 */
export function searchHelp(query: string): HelpArticle[] {
  const searchTerms = query.toLowerCase().split(' ').filter(t => t.length > 1);

  if (searchTerms.length === 0) {
    return helpArticles;
  }

  return helpArticles
    .map(article => {
      let score = 0;
      const titleLower = article.title.toLowerCase();
      const contentLower = article.content.toLowerCase();
      const summaryLower = article.summary.toLowerCase();

      for (const term of searchTerms) {
        // Title match (highest priority)
        if (titleLower.includes(term)) score += 10;
        // Keyword match (high priority)
        if (article.keywords.some(k => k.includes(term))) score += 8;
        // Summary match (medium priority)
        if (summaryLower.includes(term)) score += 5;
        // Content match (lower priority)
        if (contentLower.includes(term)) score += 2;
      }

      return { article, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ article }) => article);
}

/**
 * Get articles by category
 */
export function getArticlesByCategory(category: HelpCategory): HelpArticle[] {
  return helpArticles.filter(article => article.category === category);
}

/**
 * Get article by ID
 */
export function getArticleById(id: string): HelpArticle | undefined {
  return helpArticles.find(article => article.id === id);
}

/**
 * Get related articles
 */
export function getRelatedArticles(article: HelpArticle): HelpArticle[] {
  if (!article.relatedArticles) return [];
  return article.relatedArticles
    .map(id => getArticleById(id))
    .filter((a): a is HelpArticle => a !== undefined);
}

/**
 * Get current version
 */
export function getVersion(): string {
  return APP_VERSION;
}
